import { RecipeRealization } from '../../../api/Types';

interface SubmitRecipeRealizationParams {
  recipeId: string;
  authorId: number | null | undefined;
  canPublishRealization: boolean;
  currentAuthorRealization: RecipeRealization | null;
  commentDraft: string;
  selectedFile: File | null;
  removeCurrentImage: boolean;
  uploadRealizationImage: (file: File, authorId: number) => Promise<string>;
  upsertMyRecipeRealization: (payload: {
    recipe_id: string;
    author_id: number;
    comment: string | null;
    image: string | null;
  }) => Promise<{ comment: string | null }>;
  deleteRealizationImage: (realizationId: number, imageUrl: string) => Promise<void>;
}

interface SubmitRecipeRealizationResult {
  shouldAbort: boolean;
  errorMessage?: string;
  successMessage?: string;
  nextCommentDraft?: string;
  shouldResetDraft?: boolean;
  previousImageCleanupWarning?: unknown;
}

interface DeleteRecipeRealizationParams {
  currentAuthorRealization: RecipeRealization | null;
  deleteMyRecipeRealization: (realizationId: number) => Promise<void>;
  deleteRealizationImage: (realizationId: number, imageUrl: string) => Promise<void>;
}

interface DeleteRecipeRealizationResult {
  shouldAbort: boolean;
  errorMessage?: string;
  successMessage?: string;
  imageCleanupWarning?: unknown;
}

export const getRealizationErrorMessage = (
  error: unknown,
  fallbackMessage: string
) =>
  typeof error === 'object' &&
  error !== null &&
  'message' in error &&
  typeof (error as { message?: unknown }).message === 'string'
    ? (error as { message: string }).message
    : fallbackMessage;

export const canSubmitRecipeRealization = (
  authorId: number | null | undefined,
  canPublishRealization: boolean,
  submitting: boolean
) => Boolean(authorId && canPublishRealization && !submitting);

export const canDeleteRecipeRealization = (
  currentAuthorRealization: RecipeRealization | null,
  submitting: boolean
) => Boolean(currentAuthorRealization && !submitting);

export const shouldDeletePreviousRealizationImage = (
  previousImageUrl: string | null,
  nextImageUrl: string | null,
  currentAuthorRealization: RecipeRealization | null
) => Boolean(
  previousImageUrl &&
  previousImageUrl !== nextImageUrl &&
  currentAuthorRealization?.id
);

export const submitRecipeRealization = async ({
  recipeId,
  authorId,
  canPublishRealization,
  currentAuthorRealization,
  commentDraft,
  selectedFile,
  removeCurrentImage,
  uploadRealizationImage,
  upsertMyRecipeRealization,
  deleteRealizationImage,
}: SubmitRecipeRealizationParams): Promise<SubmitRecipeRealizationResult> => {
  if (!authorId || !canPublishRealization) {
    return { shouldAbort: true };
  }

  const previousImageUrl = currentAuthorRealization?.image || null;
  let nextImageUrl = removeCurrentImage ? null : previousImageUrl;

  try {
    if (selectedFile) {
      nextImageUrl = await uploadRealizationImage(selectedFile, authorId);
    }

    const savedRealization = await upsertMyRecipeRealization({
      recipe_id: recipeId,
      author_id: authorId,
      comment: commentDraft.trim() || null,
      image: nextImageUrl,
    });

    let previousImageCleanupWarning: unknown;

    if (
      shouldDeletePreviousRealizationImage(
        previousImageUrl,
        nextImageUrl,
        currentAuthorRealization
      )
    ) {
      try {
        await deleteRealizationImage(currentAuthorRealization!.id, previousImageUrl!);
      } catch (deleteError) {
        previousImageCleanupWarning = deleteError;
      }
    }

    return {
      shouldAbort: false,
      successMessage: currentAuthorRealization
        ? 'Realisation mise a jour.'
        : 'Realisation ajoutee.',
      nextCommentDraft: savedRealization.comment || '',
      shouldResetDraft: true,
      previousImageCleanupWarning,
    };
  } catch (error) {
    return {
      shouldAbort: true,
      errorMessage: getRealizationErrorMessage(
        error,
        'Impossible de sauvegarder la realisation.'
      ),
    };
  }
};

export const deleteRecipeRealization = async ({
  currentAuthorRealization,
  deleteMyRecipeRealization,
  deleteRealizationImage,
}: DeleteRecipeRealizationParams): Promise<DeleteRecipeRealizationResult> => {
  if (!currentAuthorRealization) {
    return { shouldAbort: true };
  }

  try {
    const imageUrlToDelete = currentAuthorRealization.image;
    await deleteMyRecipeRealization(currentAuthorRealization.id);

    let imageCleanupWarning: unknown;

    if (imageUrlToDelete) {
      try {
        await deleteRealizationImage(currentAuthorRealization.id, imageUrlToDelete);
      } catch (deleteError) {
        imageCleanupWarning = deleteError;
      }
    }

    return {
      shouldAbort: false,
      successMessage: 'Realisation supprimee.',
      imageCleanupWarning,
    };
  } catch (error) {
    return {
      shouldAbort: true,
      errorMessage: getRealizationErrorMessage(
        error,
        'Impossible de supprimer la realisation.'
      ),
    };
  }
};
